/**
 * Map-related constants
 */

// Map default configuration
export const DEFAULT_MAP_CENTER: [number, number] = [-122.4194, 37.7749]; // San Francisco
export const DEFAULT_MAP_ZOOM = 10;
export const DEFAULT_MAP_MIN_ZOOM = 0;
export const DEFAULT_MAP_MAX_ZOOM = 22;
export const DEFAULT_MAP_STYLE = 'mapbox://styles/mapbox/light-v11';
export const DEFAULT_MAP_STYLE_SATELLITE =
  'mapbox://styles/mapbox/satellite-streets-v12';

// Map layer IDs
export const MAP_LAYER_IDS = {
  SITES_CLUSTERS: 'sites-clusters',
  SITES_CLUSTER_COUNT: 'sites-cluster-count',
  SITES_UNCLUSTERED: 'sites-unclustered',
  SITES_BN_COUNT: 'sites-bn-count',
  RADIUS_CIRCLE_FILL: 'radius-circle-fill',
  RADIUS_CIRCLE_STROKE: 'radius-circle-stroke',
  ARCS_FILL: 'arcs-fill',
  ARCS_STROKE: 'arcs-stroke',
  HEATMAP_OVERLAY: 'heatmap-overlay',
} as const;

// Map source IDs
export const MAP_SOURCE_IDS = {
  SITES: 'sites',
  RADIUS_CIRCLE: 'radius-circle',
  ARCS: 'arcs',
  HEATMAP_OVERLAY: 'heatmap-overlay',
} as const;

// Map interaction IDs
export const MAP_INTERACTION_IDS = {
  SITES_CLUSTERS_CLICK: 'sites-clusters-click',
  SITES_UNCLUSTERED_CLICK: 'sites-unclustered-click',
  SITES_UNCLUSTERED_HOVER: 'sites-unclustered-hover',
  SITES_UNCLUSTERED_LEAVE: 'sites-unclustered-leave',
  ARCS_FILL_CLICK: 'arcs-fill-click',
  ARCS_FILL_CONTEXTMENU: 'arcs-fill-contextmenu',
  ARCS_FILL_HOVER: 'arcs-fill-hover',
  ARCS_FILL_LEAVE: 'arcs-fill-leave',
  MAP_BACKGROUND_CLICK: 'map-background-click',
} as const;

// Map colors
export const MAP_COLORS = {
  SITE_CLUSTER: '#101CDD',
  SITE_DEFAULT: '#025287',
  SITE_HIGHLIGHTED: '#0288D1',
  ARC_DEFAULT: '#5ebc8b',
  ARC_HIGHLIGHTED: '#7ED321',
  ARC_STROKE_DEFAULT: '#438973',
  ARC_STROKE_HIGHLIGHTED: '#9EDE73',
  RADIUS_CIRCLE: '#4285F4',
  TEXT_WHITE: '#ffffff',
} as const;

// Map styling values
export const MAP_STYLING = {
  SITE_CIRCLE_RADIUS_DEFAULT: 12,
  SITE_CIRCLE_RADIUS_HIGHLIGHTED: 14,
  SITE_CIRCLE_STROKE_WIDTH: 2,
  ARC_LINE_WIDTH_DEFAULT: 2,
  ARC_LINE_WIDTH_HIGHLIGHTED: 3,
  ARC_FILL_OPACITY_DEFAULT: 0.4,
  ARC_FILL_OPACITY_HIGHLIGHTED: 0.6,
  ARC_LINE_OPACITY: 1,
  RADIUS_CIRCLE_FILL_OPACITY: 0.1,
  RADIUS_CIRCLE_STROKE_WIDTH: 2,
  RADIUS_CIRCLE_STROKE_OPACITY: 0.6,
  HEATMAP_OVERLAY_OPACITY: 0.8,
  TEXT_SIZE: 12,
} as const;

// Map clustering configuration
export const MAP_CLUSTERING = {
  CLUSTER_MAX_ZOOM: 15,
  CLUSTER_RADIUS: 30,
  CLUSTER_STEP_RADIUS_10: 20,
  CLUSTER_STEP_RADIUS_30: 30,
} as const;

// Map thresholds
export const MAP_THRESHOLDS = {
  CENTER_CHANGE_THRESHOLD: 0.0001,
  ZOOM_CHANGE_THRESHOLD: 0.01,
} as const;

// Map marker configuration
export const MAP_MARKER = {
  REMOTE_NODE_SIZE: 40,
  REMOTE_NODE_Z_INDEX: 1000,
  REMOTE_NODE_ANCHOR: 'bottom' as const,
} as const;

// Map z-index values
export const MAP_Z_INDEX = {
  FILTER_CONTROLS: 1000,
  HEATMAP_LEGEND: 1000,
  FORM_PANEL: 1000,
  FORM_PANEL_BUTTON: 1001,
  SITE_INFO_PANEL: 2000,
} as const;

// Map position values
export const MAP_POSITION = {
  FILTER_CONTROLS_TOP: 16,
  FILTER_CONTROLS_RIGHT: 16,
  FORM_PANEL_TOP: 16,
  FORM_PANEL_LEFT: 16,
  SITE_INFO_PANEL_TOP: 280,
  SITE_INFO_PANEL_RIGHT: 16,
  HEATMAP_LEGEND_BOTTOM: 16,
  HEATMAP_LEGEND_LEFT: 16,
  HEATMAP_LEGEND_RIGHT: 16,
} as const;

// Maximum zoom for arcs
export const ARCS_MIN_ZOOM = 12;
